// src/components/layouts/app/sub/CategoryDropdown.tsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  Star,
  TrendingUp,
  Sparkles,
  Zap,
  ShoppingBag,
  Home,
  Smartphone,
  Shirt,
  Heart,
  Dumbbell,
  Utensils,
  Baby,
  Package,
  ThumbsUp,
  Grid,
  ArrowRight,
  Tag,
  Percent,
  X,
  List,
} from "lucide-react";
import { CategoryItem, EnhancedCategoryDropdownProps } from "../types";
import { UnderlineButton } from "../../../../../ui/elements/UnderlineButton";

// Types pour les produits et sous-catégories
interface Product {
  id: string;
  name: string;
  price: string;
  image: string;
  rating: number;
}

interface Subcategory {
  id: string;
  name: string;
  slug: string;
  products: Product[];
}

// Mapping d'icônes minimal
const iconMap: { [key: string]: React.ReactNode } = {
  "maison-jardin": <Home className="w-4 h-4" />,
  electronique: <Smartphone className="w-4 h-4" />,
  "mode-homme": <Shirt className="w-4 h-4" />,
  "mode-femme": <Heart className="w-4 h-4" />,
  "beaute-sante": <Sparkles className="w-4 h-4" />,
  "sport-loisirs": <Dumbbell className="w-4 h-4" />,
  alimentation: <Utensils className="w-4 h-4" />,
  "enfants-bebe": <Baby className="w-4 h-4" />,
  default: <ShoppingBag className="w-4 h-4" />,
};

// Données allégées avec typage explicite
const subcategoriesData: Record<string, Subcategory[]> = {
  electronique: [
    {
      id: "e1",
      name: "Smartphones",
      slug: "smartphones",
      products: [
        { id: "p1", name: "iPhone 15", price: "1099€", image: "https://images.unsplash.com/photo-1695048133142-1a20484d2569?w=80&h=80&fit=crop", rating: 4.8 },
        { id: "p2", name: "Galaxy S24", price: "899€", image: "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=80&h=80&fit=crop", rating: 4.7 },
      ],
    },
    {
      id: "e2",
      name: "Ordinateurs",
      slug: "ordinateurs",
      products: [
        { id: "p3", name: "MacBook Pro", price: "2299€", image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=80&h=80&fit=crop", rating: 4.9 },
      ],
    },
  ],
  "mode-homme": [
    {
      id: "mh1",
      name: "Vêtements",
      slug: "vetements",
      products: [
        { id: "p4", name: "Chemise blanche", price: "79€", image: "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=80&h=80&fit=crop", rating: 4.5 },
      ],
    },
  ],
  "mode-femme": [
    {
      id: "mf1",
      name: "Robes",
      slug: "robes",
      products: [
        { id: "p5", name: "Robe d'été", price: "89€", image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=80&h=80&fit=crop", rating: 4.6 },
      ],
    },
  ],
  "maison-jardin": [
    {
      id: "mj1",
      name: "Meubles",
      slug: "meubles",
      products: [
        { id: "p6", name: "Canapé velours", price: "899€", image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=80&h=80&fit=crop", rating: 4.7 },
      ],
    },
  ],
  "beaute-sante": [
    {
      id: "bs1",
      name: "Soins visage",
      slug: "soins",
      products: [
        { id: "p7", name: "Crème hydratante", price: "49€", image: "https://images.unsplash.com/photo-1556228578-9c360e1d8d34?w=80&h=80&fit=crop", rating: 4.4 },
      ],
    },
  ],
  "sport-loisirs": [
    {
      id: "sl1",
      name: "Fitness",
      slug: "fitness",
      products: [
        { id: "p8", name: "Haltères 10kg", price: "129€", image: "https://images.unsplash.com/photo-1534367507877-0edd93bd013b?w=80&h=80&fit=crop", rating: 4.5 },
      ],
    },
  ],
  alimentation: [
    {
      id: "a1",
      name: "Bio",
      slug: "bio",
      products: [
        { id: "p9", name: "Café arabica", price: "15€", image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=80&h=80&fit=crop", rating: 4.8 },
      ],
    },
  ],
  "enfants-bebe": [
    {
      id: "eb1",
      name: "Jouets",
      slug: "jouets",
      products: [
        { id: "p10", name: "Peluche ours", price: "29€", image: "https://images.unsplash.com/photo-1589874186480-ecd6c7f0e6ac?w=80&h=80&fit=crop", rating: 4.9 },
      ],
    },
  ],
};

const CategoryDropdown: React.FC<EnhancedCategoryDropdownProps> = ({
  categories,
  isCategoryDropdownOpen,
  setIsCategoryDropdownOpen,
  onCategorySelect,
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(categories[0]?.id || null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [overlayTop, setOverlayTop] = useState(0);

  const handleMouseEnter = (categoryId?: string) => {
    if (timeoutId) clearTimeout(timeoutId);
    if (categoryId) {
      const id = setTimeout(() => setActiveCategory(categoryId), 30);
      setTimeoutId(id);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsCategoryDropdownOpen]);

  useEffect(() => {
    if (isCategoryDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOverlayTop(rect.bottom + window.scrollY);
    }
  }, [isCategoryDropdownOpen]);

  const handleCategoryClick = (category: CategoryItem) => {
    setActiveCategory(category.id);
    onCategorySelect?.(category);
  };

  const activeCategoryData = activeCategory
    ? categories.find((c) => c.id === activeCategory)
    : categories[0] || null;

  const activeSubcategories: Subcategory[] = activeCategoryData
    ? subcategoriesData[activeCategoryData.slug] || []
    : [];

  return (
    <div
      ref={dropdownRef}
      className="px-0"
      onMouseEnter={() => {
        if (timeoutId) clearTimeout(timeoutId);
        setIsCategoryDropdownOpen(true);
      }}
      onMouseLeave={() => {
        handleMouseLeave();
        const id = setTimeout(() => setIsCategoryDropdownOpen(false), 200);
        setTimeoutId(id);
      }}
    >
      <UnderlineButton
        ref={buttonRef}
        isActive={isCategoryDropdownOpen}
        onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
        activeColor="bg-purple-500"
        inactiveColor="bg-purple-500"
        className="text-gray-700 hover:text-purple-600"
      >
        <List className="w-4 h-4" />
        <span className="font-semibold text-sm tracking-wide">CATÉGORIES</span>
        <ChevronRight
          className={`w-4 h-4 transition-transform duration-200 ${
            isCategoryDropdownOpen ? "rotate-90" : ""
          }`}
        />
      </UnderlineButton>

      <AnimatePresence>
        {isCategoryDropdownOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
              style={{ top: overlayTop }}
              onClick={() => setIsCategoryDropdownOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: 8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: 8, height: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute left-0 right-0 top-full -mt-1.5 bg-white z-50 border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              onMouseEnter={() => timeoutId && clearTimeout(timeoutId)}
              onMouseLeave={() => {
                handleMouseLeave();
                const id = setTimeout(() => setIsCategoryDropdownOpen(false), 150);
                setTimeoutId(id);
              }}
            >
              <div className="container mx-auto">


                <div className="px-6 py-5">
                  <div className="flex gap-6">
                    {/* Colonne catégories */}
                    <div className="w-64 border-r border-gray-100 pr-4">
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                          <Grid className="w-3.5 h-3.5 text-purple-500" />
                          Toutes catégories
                        </h3>
                      </div>
                      <div className="space-y-1">
                        {categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleCategoryClick(category)}
                            onMouseEnter={() => handleMouseEnter(category.id)}
                            className={`
                              w-full flex items-center justify-between px-3 py-2 rounded-lg text-left
                              transition-all duration-150
                              ${
                                activeCategory === category.id
                                  ? "bg-purple-50 text-purple-700 border-l-2 border-purple-500"
                                  : "text-gray-700 hover:bg-gray-50"
                              }
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">{iconMap[category.slug] || iconMap.default}</span>
                              <span className="text-sm font-medium">{category.name}</span>
                            </div>
                            <ChevronRight
                              className={`w-3.5 h-3.5 transition-all ${
                                activeCategory === category.id ? "text-purple-500 translate-x-0.5" : "text-gray-400"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Colonne produits */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-base font-bold text-gray-900">
                            {activeCategoryData?.name}
                          </h2>
                          <p className="text-xs text-gray-500">Découvrez nos meilleures offres</p>
                        </div>
                        <div className="bg-gray-100 rounded-full px-2.5 py-1 text-xs text-gray-600">
                          {activeSubcategories.reduce((acc, sub) => acc + sub.products.length, 0)} produits
                        </div>
                      </div>

                      {/* Grille compacte */}
                      <div className="grid grid-cols-2 gap-4 max-h-[420px] overflow-y-auto pr-2">
                        {activeSubcategories.map((sub) => (
                          <div key={sub.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-semibold text-sm">{sub.name}</span>
                              <button
                                onClick={() => console.log("Voir plus", sub.slug)}
                                className="text-xs text-purple-600 hover:underline"
                              >
                                Tout voir
                              </button>
                            </div>
                            <div className="space-y-2">
                              {sub.products.map((product: Product) => (
                                <div key={product.id} className="flex items-center gap-2 bg-white p-2 rounded-md">
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-10 h-10 rounded object-cover"
                                    onError={(e) => ((e.target as HTMLImageElement).src = "https://via.placeholder.com/40")}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{product.name}</p>
                                    <div className="flex items-center justify-between mt-1">
                                      <span className="text-xs font-bold">{product.price}</span>
                                      <div className="flex items-center gap-0.5">
                                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                        <span className="text-[10px]">{product.rating}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Mini bannière */}
                      <div className="mt-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-3 text-white flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Percent className="w-4 h-4" />
                          <span className="text-xs font-medium">Promos jusqu'à -40%</span>
                        </div>
                        <button className="bg-white/20 text-xs px-3 py-1 rounded-full hover:bg-white/30">Voir</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryDropdown;